import { useMemo, useState } from 'react';
import type { Card, Colaborador, Flow } from '../../types';

// 📅 Agenda dos compradores (14/10/2026, fluxo Compras Revenda do Evandro):
// semana a semana, os cards pela data de um campo de data (padrão: o que tem
// "agend" no nome), com quem é o responsável e o que está atrasado.
// A aba só aparece em fluxo que tem campo de data com "agend" no nome.
// 14/10: além da semana, visão de mês e lista de período longo (30 a 365 dias),
// para o comprador planejar compras programadas.

interface AgendaFluxoProps {
  flow: Flow;
  cards: Card[];
  colaboradores?: Colaborador[];
  onAbrirCard: (card: Card) => void;
}

type Modo = 'semana' | 'mes' | 'lista';
const DIAS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const FINAL = /conclu|reprov|cancel|finaliz|arquiv/i;

export const camposDataAgenda = (flow: Flow) =>
  (flow.etapas || []).flatMap(e => (e.campos || []).filter(c => c.tipo === 'data' || c.tipo === 'data_vencimento')
    .map(c => ({ id: String(c.id), nome: c.nome, etapa: e.nome })));

export const fluxoTemAgenda = (flow: Flow) => camposDataAgenda(flow).some(c => /agend/i.test(c.nome));

// aceita 'AAAA-MM-DD', ISO e 'DD/MM/AAAA'; devolve a data local (meia-noite) ou null
const lerData = (v: unknown): Date | null => {
  if (!v) return null;
  const s = String(typeof v === 'object' && v !== null && 'valor' in (v as any) ? (v as any).valor : v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
const inicioSemana = (d: Date) => { const x = new Date(d); const dia = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dia); x.setHours(0, 0, 0, 0); return x; };
const mesmoDia = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const iniciais = (nome: string) => nome.split(' ').filter(p => p.length > 2).map(p => p[0]).join('').slice(0, 2).toUpperCase() || nome.slice(0, 2).toUpperCase();

export default function AgendaFluxo({ flow, cards, colaboradores = [], onAbrirCard }: AgendaFluxoProps) {
  const campos = useMemo(() => camposDataAgenda(flow), [flow]);
  const [campoId, setCampoId] = useState(() => (campos.find(c => /agend/i.test(c.nome)) || campos[0])?.id || '');
  const [quem, setQuem] = useState('');
  const [semana, setSemana] = useState(() => inicioSemana(new Date()));
  const [modo, setModo] = useState<Modo>('semana');
  const [mes, setMes] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [periodo, setPeriodo] = useState(90);
  const hoje = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const etapaNome = useMemo(() => new Map((flow.etapas || []).map(e => [String(e.id), e.nome])), [flow]);
  const nomeDe = (id?: string) => colaboradores.find(c => String(c.id) === String(id || ''))?.nome || '';
  const campoAtividade = useMemo(() => (flow.etapas || []).flatMap(e => e.campos || []).find(c => /atividade/i.test(c.nome))?.id, [flow]);
  const campoAssunto = useMemo(() => (flow.etapas || []).flatMap(e => e.campos || []).find(c => /assunto/i.test(c.nome))?.id, [flow]);

  const itens = useMemo(() => cards.map(card => {
    const data = lerData((card.campos as any)?.[campoId]);
    const etapa = etapaNome.get(String(card.etapaId)) || '';
    return { card, data, etapa, encerrado: FINAL.test(etapa), resp: String((card as any).responsavelIndividualId || '') };
  }).filter(i => i.data && (!quem || i.resp === quem)), [cards, campoId, quem, etapaNome]);

  const dias = Array.from({ length: 7 }, (_, i) => { const d = new Date(semana); d.setDate(d.getDate() + i); return d; });
  const fimSemana = dias[6];
  const atrasados = itens.filter(i => !i.encerrado && i.data! < hoje).sort((a, b) => +a.data! - +b.data!);
  const daSemana = itens.filter(i => i.data! >= semana && i.data! <= fimSemana);
  const deHoje = itens.filter(i => mesmoDia(i.data!, hoje) && !i.encerrado);
  // mês: semanas de segunda a domingo que cobrem o mês inteiro
  const diasMes = useMemo(() => {
    const ini = inicioSemana(mes); const fim = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
    const out: Date[] = []; for (const d = new Date(ini); d <= fim || out.length % 7; d.setDate(d.getDate() + 1)) out.push(new Date(d));
    return out;
  }, [mes]);
  const doMes = itens.filter(i => i.data!.getMonth() === mes.getMonth() && i.data!.getFullYear() === mes.getFullYear());
  // período: de hoje até hoje + N dias, agrupado por dia
  const fimPeriodo = useMemo(() => { const d = new Date(hoje); d.setDate(d.getDate() + periodo); return d; }, [hoje, periodo]);
  const daLista = itens.filter(i => i.data! >= hoje && i.data! <= fimPeriodo).sort((a, b) => +a.data! - +b.data!);
  const gruposLista: (typeof itens)[] = [];
  daLista.forEach(i => { const g = gruposLista[gruposLista.length - 1]; if (g && mesmoDia(g[0].data!, i.data!)) g.push(i); else gruposLista.push([i]); });
  const anda = (n: number) => {
    if (modo === 'mes') setMes(m => new Date(m.getFullYear(), m.getMonth() + n, 1));
    else setSemana(s => { const d = new Date(s); d.setDate(d.getDate() + 7 * n); return d; });
  };
  const volta = () => { setSemana(inicioSemana(new Date())); const d = new Date(); setMes(new Date(d.getFullYear(), d.getMonth(), 1)); };
  const responsaveis = useMemo(() => {
    const ids = Array.from(new Set(cards.map(c => String((c as any).responsavelIndividualId || '')).filter(Boolean)));
    return ids.map(id => ({ id, nome: nomeDe(id) || 'Sem nome' })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [cards, colaboradores]); // eslint-disable-line react-hooks/exhaustive-deps

  const Chip = ({ i, atraso }: { i: typeof itens[number]; atraso?: boolean }) => {
    const nome = nomeDe(i.resp);
    const atividade = campoAtividade ? (i.card.campos as any)?.[campoAtividade] : '';
    const assunto = campoAssunto ? (i.card.campos as any)?.[campoAssunto] : '';
    return (
      <button onClick={() => onAbrirCard(i.card)}
        className={`w-full text-left rounded-lg border px-2.5 py-2 mb-2 bg-white hover:border-gray-900 transition-colors ${atraso ? 'border-red-300 bg-red-50' : i.encerrado ? 'border-gray-100 opacity-60' : 'border-gray-200'}`}>
        <div className="text-[13px] font-semibold text-gray-900 leading-snug line-clamp-2">{i.card.titulo}</div>
        {(atividade || assunto) && <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-1">{[atividade, assunto].filter(Boolean).join(' · ')}</div>}
        <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-gray-500">
          {nome && <span className="w-5 h-5 rounded-full bg-gray-900 text-white grid place-items-center text-[9px] font-bold flex-none" title={nome}>{iniciais(nome)}</span>}
          <span className="truncate">{i.etapa}</span>
          {atraso && <span className="ml-auto text-red-600 font-semibold flex-none">{i.data!.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>}
        </div>
      </button>
    );
  };

  if (!campos.length) return <div className="p-8 text-sm text-gray-500">Este fluxo não tem campo de data para montar a agenda.</div>;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="px-5 py-3 bg-white border-b border-gray-100 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          {([['semana', 'Semana'], ['mes', 'Mês'], ['lista', 'Período']] as [Modo, string][]).map(([m, rot]) => (
            <button key={m} onClick={() => setModo(m)} className={`px-3 py-1.5 ${modo === m ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{rot}</button>
          ))}
        </div>
        {modo !== 'lista' ? (<>
          <div className="flex items-center gap-1">
            <button onClick={() => anda(-1)} className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">‹</button>
            <button onClick={volta} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">Hoje</button>
            <button onClick={() => anda(1)} className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">›</button>
          </div>
          <div className={`text-sm font-semibold text-gray-900 ${modo === 'mes' ? 'capitalize' : ''}`}>
            {modo === 'mes' ? mes.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
              : `${semana.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} – ${fimSemana.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}`}
          </div>
        </>) : (
          <select value={periodo} onChange={e => setPeriodo(Number(e.target.value))} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
            {[30, 60, 90, 180, 365].map(p => <option key={p} value={p}>próximos {p} dias</option>)}
          </select>
        )}
        <div className="flex gap-2 ml-2 text-xs">
          <span className="px-2.5 py-1 rounded-full bg-gray-900 text-white">Hoje: {deHoje.length}</span>
          <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
            {modo === 'semana' ? `Na semana: ${daSemana.length}` : modo === 'mes' ? `No mês: ${doMes.length}` : `No período: ${daLista.length}`}
          </span>
          <span className={`px-2.5 py-1 rounded-full ${atrasados.length ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>Vencidos: {atrasados.length}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select value={quem} onChange={e => setQuem(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
            <option value="">Todos os responsáveis</option>
            {responsaveis.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
          </select>
          {campos.length > 1 && (
            <select value={campoId} onChange={e => setCampoId(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white" title="Campo de data usado na agenda">
              {campos.map(c => <option key={c.id} value={c.id}>{c.nome} · {c.etapa}</option>)}
            </select>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 flex gap-3 p-4 overflow-x-auto">
        <div className="w-60 flex-none bg-white rounded-xl border border-red-100 flex flex-col min-h-0">
          <div className="px-3 py-2.5 border-b border-red-100">
            <div className="text-sm font-semibold text-red-700">Prazo vencido</div>
            <div className="text-[11px] text-gray-500">data já passou e o card segue aberto</div>
          </div>
          <div className="p-2 overflow-y-auto">
            {atrasados.length ? atrasados.map(i => <Chip key={i.card.id} i={i} atraso />) : <div className="text-xs text-gray-400 p-2">Nada vencido.</div>}
          </div>
        </div>
        {modo === 'mes' && (
          <div className="flex-1 min-w-[640px] bg-white rounded-xl border border-gray-100 flex flex-col min-h-0">
            <div className="grid grid-cols-7 border-b border-gray-100 text-[11px] uppercase tracking-wide text-gray-500">
              {DIAS.map(d => <div key={d} className="px-2 py-1.5">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 flex-1 overflow-y-auto" style={{ gridAutoRows: 'minmax(96px, 1fr)' }}>
              {diasMes.map((d, k) => {
                const doDia = itens.filter(i => mesmoDia(i.data!, d));
                const fora = d.getMonth() !== mes.getMonth();
                return (
                  <div key={k} className={`border-b border-r border-gray-100 p-1.5 min-w-0 ${fora ? 'bg-gray-50/70' : ''} ${mesmoDia(d, hoje) ? 'ring-1 ring-inset ring-gray-900' : ''}`}>
                    <div className={`text-[11px] mb-1 ${fora ? 'text-gray-300' : 'text-gray-600'}`}>{d.getDate()}</div>
                    {doDia.slice(0, 3).map(i => (
                      <button key={i.card.id} onClick={() => onAbrirCard(i.card)} title={`${i.card.titulo} · ${i.etapa}${nomeDe(i.resp) ? ' · ' + nomeDe(i.resp) : ''}`}
                        className={`block w-full text-left truncate text-[11px] px-1.5 py-0.5 mb-0.5 rounded ${!i.encerrado && i.data! < hoje ? 'bg-red-50 text-red-700' : i.encerrado ? 'bg-gray-50 text-gray-400' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                        {nomeDe(i.resp) && <b className="mr-1">{iniciais(nomeDe(i.resp))}</b>}{i.card.titulo}
                      </button>))}
                    {doDia.length > 3 && <button onClick={() => { setModo('semana'); setSemana(inicioSemana(d)); }} className="text-[11px] text-gray-500 hover:text-gray-900">+{doDia.length - 3} mais</button>}
                  </div>);
              })}
            </div>
          </div>
        )}
        {modo === 'lista' && (
          <div className="flex-1 min-w-[320px] bg-white rounded-xl border border-gray-100 overflow-y-auto">
            {!gruposLista.length && <div className="text-sm text-gray-400 p-4">Nada agendado nos próximos {periodo} dias.</div>}
            {gruposLista.map(g => (
              <div key={g[0].data!.toDateString()} className="border-b border-gray-100">
                <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 capitalize">
                  {g[0].data!.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
                  <span className="ml-2 font-normal text-gray-500">{g.length}</span>
                </div>
                <div className="p-2 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                  {g.map(i => <Chip key={i.card.id} i={i} />)}
                </div>
              </div>))}
          </div>
        )}
        {modo === 'semana' && dias.map((d, k) => {
          const doDia = daSemana.filter(i => mesmoDia(i.data!, d));
          const ehHoje = mesmoDia(d, hoje);
          return (
            <div key={k} className={`min-w-[180px] flex-1 rounded-xl border flex flex-col min-h-0 ${ehHoje ? 'bg-white border-gray-900' : k > 4 ? 'bg-gray-100/60 border-gray-100' : 'bg-white border-gray-100'}`}>
              <div className="px-3 py-2.5 border-b border-gray-100 flex items-baseline gap-2">
                <span className={`text-sm font-semibold ${ehHoje ? 'text-gray-900' : 'text-gray-700'}`}>{DIAS[k]}</span>
                <span className="text-xs text-gray-500">{d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                {doDia.length > 0 && <span className="ml-auto text-[11px] text-gray-500">{doDia.length}</span>}
              </div>
              <div className="p-2 overflow-y-auto">{doDia.map(i => <Chip key={i.card.id} i={i} />)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
